"use client";
import React, { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  structuredData?: Record<string, unknown>;
}

const SEO: React.FC<SEOProps> = ({ title, description, keywords, structuredData }) => {
  const serializedStructuredData = structuredData
    ? JSON.stringify(structuredData)
    : '';

  useEffect(() => {
    document.title = title;

    const upsertMeta = (
      selector: string,
      attribute: 'name' | 'property',
      key: string,
      content: string,
    ) => {
      let element = document.head.querySelector<HTMLMetaElement>(selector);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    upsertMeta('meta[name="description"]', 'name', 'description', description);
    if (keywords) {
      upsertMeta('meta[name="keywords"]', 'name', 'keywords', keywords);
    }
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    upsertMeta(
      'meta[property="og:description"]',
      'property',
      'og:description',
      description,
    );
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', 'article');

    const structuredDataSelector = 'script[data-seo-structured-data="true"]';
    let structuredDataScript = document.head.querySelector<HTMLScriptElement>(
      structuredDataSelector,
    );

    if (serializedStructuredData) {
      if (!structuredDataScript) {
        structuredDataScript = document.createElement('script');
        structuredDataScript.type = 'application/ld+json';
        structuredDataScript.dataset.seoStructuredData = 'true';
        document.head.appendChild(structuredDataScript);
      }
      structuredDataScript.textContent = serializedStructuredData;
    } else {
      structuredDataScript?.remove();
    }

    return () => {
      document.head
        .querySelector<HTMLScriptElement>(structuredDataSelector)
        ?.remove();
    };
  }, [description, keywords, serializedStructuredData, title]);

  return null;
};

export default SEO;
